'use client';

import React, { useState, useRef, useEffect } from 'react';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, getWeek } from 'date-fns';
import { ChevronLeft, ChevronRight, ChevronDown, Search, Plus, Check } from 'lucide-react';
import { cn } from '@/app/components/lib/utils';
import { CALENDAR_VIEW_OPTIONS } from './constants';
export const CalendarHeader = ({
  currentDate,
  view,
  onViewChange,
  onPrev,
  onNext,
  onToday,
  onAddEvent,
  onSearchClick,
  className,
  slotProps
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown on click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard shortcuts ⌘D, ⌘W, ⌘M
  useEffect(() => {
    function handleKeyDown(e) {
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey) {
        if (e.key === 'd' || e.key === 'D') {
          e.preventDefault();
          onViewChange('day');
        } else if (e.key === 'w' || e.key === 'W') {
          e.preventDefault();
          onViewChange('week');
        } else if (e.key === 'm' || e.key === 'M') {
          e.preventDefault();
          onViewChange('month');
        }
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onViewChange]);

  // Subtitle date range
  const subtitle = (() => {
    if (view === 'month') {
      const s = startOfMonth(currentDate);
      const e = endOfMonth(currentDate);
      return `${format(s, 'MMM d, yyyy')} – ${format(e, 'MMM d, yyyy')}`;
    }
    if (view === 'week') {
      const s = startOfWeek(currentDate, {
        weekStartsOn: 1
      });
      const e = endOfWeek(currentDate, {
        weekStartsOn: 1
      });
      return `${format(s, 'MMM d')} – ${format(e, 'MMM d, yyyy')}`;
    }
    return format(currentDate, 'EEEE, MMMM d, yyyy');
  })();
  const weekNum = getWeek(currentDate, {
    weekStartsOn: 1
  });
  const activeViewOption = CALENDAR_VIEW_OPTIONS.find(opt => opt.value === view);
  const headerSlot = slotProps?.header;
  return <header {...headerSlot?.root} className={cn('relative flex flex-col items-start justify-between gap-4 border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 px-5 py-4 sm:px-6 lg:flex-row lg:items-center', className, headerSlot?.root?.className)}>
            {/* Left: Date badge, Title, Subtitle, Week badge */}
            <div className="flex items-center gap-3.5">
                {/* Mini Date Card Badge */}
                <div {...headerSlot?.dateBadge} className={cn('hidden sm:inline-flex min-w-14 flex-col items-center overflow-hidden rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-850 shadow-2xs', headerSlot?.dateBadge?.className)}>
                    <div className="flex w-full justify-center bg-zinc-100 dark:bg-zinc-800 px-2 py-0.5">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">{format(currentDate, 'MMM')}</span>
                    </div>
                    <div className="flex w-full justify-center px-2 py-1">
                        <span className="text-base font-bold text-zinc-900 dark:text-zinc-100">{format(currentDate, 'd')}</span>
                    </div>
                </div>

                {/* Title & dynamic range */}
                <div {...headerSlot?.titleGroup} className={cn('flex flex-col gap-0.5', headerSlot?.titleGroup?.className)}>
                    <div className="flex items-center gap-2">
                        <h2 {...headerSlot?.title} className={cn('text-lg font-bold tracking-tight text-zinc-900 dark:text-zinc-100 whitespace-nowrap', headerSlot?.title?.className)}>
                            {format(currentDate, 'MMMM yyyy')}
                        </h2>
                        <span {...headerSlot?.weekBadge} className={cn('inline-flex items-center rounded-md bg-zinc-100 dark:bg-zinc-800 px-2 py-0.5 text-xs font-semibold text-zinc-600 dark:text-zinc-300 ring-1 ring-inset ring-zinc-200 dark:ring-zinc-700', headerSlot?.weekBadge?.className)}>
                            Week {weekNum}
                        </span>
                    </div>
                    <p {...headerSlot?.subtitle} className={cn('text-xs text-zinc-500 dark:text-zinc-400 font-medium', headerSlot?.subtitle?.className)}>
                        {subtitle}
                    </p>
                </div>
            </div>

            {/* Right: Controls & Actions */}
            <div {...headerSlot?.actionsGroup} className={cn('flex flex-wrap items-center gap-2.5 max-sm:w-full max-sm:justify-between', headerSlot?.actionsGroup?.className)}>
                {/* Search button */}
                <button type="button" onClick={onSearchClick} aria-label="Search events" {...headerSlot?.searchButton} className={cn('flex size-9 items-center justify-center rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-850 text-zinc-600 dark:text-zinc-300 shadow-2xs hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors', headerSlot?.searchButton?.className)}>
                    <Search className="size-4 stroke-[2.25px]" />
                </button>

                {/* ButtonGroup: Prev, Today, Next */}
                <div {...headerSlot?.buttonGroup} className={cn('inline-flex rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-850 shadow-2xs', headerSlot?.buttonGroup?.className)}>
                    <button type="button" onClick={onPrev} aria-label="Previous" {...headerSlot?.prevButton} className={cn('flex size-9 items-center justify-center rounded-l-lg text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 border-r border-zinc-200 dark:border-zinc-700 transition-colors', headerSlot?.prevButton?.className)}>
                        <ChevronLeft className="size-4 stroke-[2.25px]" />
                    </button>
                    <button type="button" onClick={onToday} {...headerSlot?.todayButton} className={cn('px-3.5 text-xs font-semibold text-zinc-700 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-800 border-r border-zinc-200 dark:border-zinc-700 transition-colors', headerSlot?.todayButton?.className)}>
                        Today
                    </button>
                    <button type="button" onClick={onNext} aria-label="Next" {...headerSlot?.nextButton} className={cn('flex size-9 items-center justify-center rounded-r-lg text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors', headerSlot?.nextButton?.className)}>
                        <ChevronRight className="size-4 stroke-[2.25px]" />
                    </button>
                </div>

                {/* View Switcher Dropdown */}
                <div ref={dropdownRef} className="relative">
                    <button type="button" onClick={() => setDropdownOpen(!dropdownOpen)} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-850 px-3 text-xs font-semibold text-zinc-700 dark:text-zinc-200 shadow-2xs hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors">
                        <span>{activeViewOption?.label}</span>
                        <ChevronDown className="size-3.5 stroke-[2.25px] text-zinc-400" />
                    </button>

                    {dropdownOpen && <div className="absolute right-0 z-50 mt-1.5 w-48 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-850 p-1 shadow-lg animate-in fade-in zoom-in-95 duration-100">
                            {CALENDAR_VIEW_OPTIONS.map(opt => <button key={opt.value} type="button" onClick={() => {
            onViewChange(opt.value);
            setDropdownOpen(false);
          }} className={cn('flex w-full items-center justify-between rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors', view === opt.value ? 'bg-purple-50 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300 font-semibold' : 'text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800')}>
                                    <div className="flex items-center gap-2">
                                        {view === opt.value && <Check className="size-3.5 text-purple-600 dark:text-purple-400" />}
                                        <span className={view === opt.value ? 'ml-0' : 'ml-5'}>{opt.label}</span>
                                    </div>
                                    <span className="text-[10px] font-mono text-zinc-400 dark:text-zinc-500">{opt.addon}</span>
                                </button>)}
                        </div>}
                </div>

                {/* Add Event Button */}
                <button type="button" onClick={() => onAddEvent?.()} {...headerSlot?.addEventButton} className={cn('inline-flex h-9 items-center gap-1.5 rounded-lg bg-purple-600 hover:bg-purple-700 dark:bg-purple-600 dark:hover:bg-purple-500 px-3.5 text-xs font-semibold text-white shadow-2xs transition-colors', headerSlot?.addEventButton?.className)}>
                    <Plus className="size-4 stroke-[2.5px]" />
                    <span>Add event</span>
                </button>
            </div>
        </header>;
};